// student-dashboard.tsx — Student portal shell: nav + history-backed routing.
import { useCallback } from 'react';
import { useAuthStore } from '../../services/authentication/auth-store';
import { useHistoryLocation } from '../../hooks/use-history-location';
import type { StudentLocation } from './student-location';
import { StudentNav } from './layout/student-nav';
import { StudentHome } from './home/student-home';
import { StudentSessionsPage } from './student-sessions-page';
import { StudentPortfolioPage } from './portfolio/student-portfolio-page';
import { StudentCheatsPage } from './cheats/student-cheats-page';
import { StudentCampaignsPage } from './campaigns/student-campaigns-page';
import { useStudentProgress } from '../../api/student/progress/hooks';
import { isStudentCheatsEnabled } from '../../config/environment';
import './styles/index.css';

export type StudentView =
  | 'home'
  | 'sessions'
  | 'campaigns'
  | 'portfolio'
  | 'cheats';

export function StudentDashboard() {
  const user = useAuthStore((state) => state.user);
  const { location, navigate, back } = useHistoryLocation<StudentLocation>({ kind: 'home' });

  const progress = useStudentProgress().data ?? null;
  const avatarPhotoUrl = progress?.avatarPhotoUrl ?? null;

  // The campaigns area covers the list + any campaign/chapter/activity/material.
  // A 'cheats' location outside local/develop (e.g. a stale history entry)
  // falls back to home instead of rendering the dev tooling.
  const activeView: StudentView =
    location.kind === 'home' ? 'home'
    : location.kind === 'sessions' ? 'sessions'
    : location.kind === 'portfolio' ? 'portfolio'
    : location.kind === 'cheats' ? (isStudentCheatsEnabled() ? 'cheats' : 'home')
    : 'campaigns';

  const goView = useCallback(
    (next: StudentView) => navigate(next === 'campaigns' ? { kind: 'campaigns' } : { kind: next }),
    [navigate],
  );

  return (
    <div className="portal-shell student-portal">
      <StudentNav
        activeView={activeView}
        onNavigate={goView}
        userName={user?.name ?? ''}
        firstName={user?.firstName ?? ''}
        avatarPhotoUrl={avatarPhotoUrl}
        level={progress?.level}
      />

      <main>
        {activeView === 'home' && (
          <StudentHome
            userName={user?.name ?? ''}
            firstName={user?.firstName ?? ''}
            onNavigate={goView}
            onOpenCampaign={(campaignId) => navigate({ kind: 'campaign', campaignId })}
            navigate={navigate}
          />
        )}
        {activeView === 'sessions' && <StudentSessionsPage />}
        {activeView === 'campaigns' && (
          <StudentCampaignsPage location={location} navigate={navigate} back={back} />
        )}
        {activeView === 'portfolio' && <StudentPortfolioPage navigate={navigate} />}
        {activeView === 'cheats' && (
          <StudentCheatsPage progress={progress} />
        )}
      </main>
    </div>
  );
}
